import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Lock } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-white rounded-lg border border-[#E1E7EF] shadow-sm p-8">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-[#E8F0FE] rounded-full mb-4">
            <Lock className="w-5 h-5 text-[#0056D2]" />
          </div>
          <h1 className="font-heading font-bold text-xl text-[#1F1F1F]">Admin Login</h1>
          <p className="font-body text-sm text-[#585858] mt-1">A7 Globetech LLP</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 font-body rounded-md" data-testid="login-error">
              {error}
            </div>
          )}
          <div>
            <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Email</label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="border-[#E1E7EF] focus:border-[#0056D2] focus:ring-[#0056D2]/20 rounded-md h-11"
              placeholder="admin@a7globetech.com"
            />
          </div>
          <div>
            <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Password</label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="border-[#E1E7EF] focus:border-[#0056D2] focus:ring-[#0056D2]/20 rounded-md h-11"
              placeholder="Enter password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full font-body font-semibold text-sm bg-[#0056D2] text-white py-3 rounded-md hover:bg-[#003E99] transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div className="mt-6 text-center">
          <a href="/" className="font-body text-sm text-[#0056D2] hover:underline">
            &larr; Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
